import { useEffect, useState } from "react";

/**
 * Captures a promotional code from the URL (`?promo=...`) and remembers it for
 * 30 days so it survives navigation and signup. The code itself carries no
 * authority — the checkout edge function validates it server-side before any
 * discounted price is used.
 */
const STORAGE_KEY = "echocrm_promo";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Display metadata for each live offer. The authoritative code -> Stripe price
 * mapping lives in the `create-checkout` edge function.
 */
export const PROMO_OFFERS: Record<
  string,
  { priceLabel: string; headline: string }
> = {
  lifetime29: {
    priceLabel: "$29",
    headline: "Lifetime deal — Pro at $29/mo, locked in for as long as your subscription stays active.",
  },
};

export const KNOWN_PROMOS = Object.keys(PROMO_OFFERS);

/**
 * Hostnames that automatically activate a promo, so ad traffic landing on a
 * dedicated marketing subdomain gets the offer without a query string.
 */
export const PROMO_HOSTNAMES: Record<string, string> = {
  "try.echocrm.app": "lifetime29",
  "www.try.echocrm.app": "lifetime29",
};

function promoFromHostname(): string | null {
  try {
    return PROMO_HOSTNAMES[window.location.hostname.toLowerCase()] ?? null;
  } catch {
    return null;
  }
}

function readStored(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { code: string; savedAt: number };
    if (!parsed?.code || Date.now() - parsed.savedAt > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.code;
  } catch {
    return null;
  }
}

export function getPromoCode(): string | null {
  return readStored() ?? promoFromHostname();
}

export function clearPromoCode() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export const usePromo = () => {
  const [promoCode, setPromoCode] = useState<string | null>(
    () => readStored() ?? promoFromHostname()
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("promo") ?? promoFromHostname();
    if (!raw) return;
    const code = raw.trim().toLowerCase().slice(0, 32);
    if (!code) return;
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ code, savedAt: Date.now() })
      );
    } catch {
      /* ignore */
    }
    setPromoCode(code);
  }, []);

  return {
    promoCode,
    isPromoActive: promoCode ? Boolean(PROMO_OFFERS[promoCode]) : false,
    offer: promoCode ? PROMO_OFFERS[promoCode] ?? null : null,
  };
};